import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import RowActionsMenu from '../../Components/RowActionsMenu';

const API_BASE = `${API_BASE_URL}/seller/api/sub-product-items`;

const SubProductItemManagement = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        localStorage.removeItem('user');
        navigate('/login');
    }, [navigate]);

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) {
            handleLogout();
            return null;
        }
        return JSON.parse(user)?.token;
    }, [handleLogout]);

    const fetchItems = useCallback(async () => {
        try {
            const token = checkToken();
            if (!token) return;

            const response = await fetch(API_BASE, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 403) {
                    handleLogout();
                    return;
                }
                const errorText = await response.text();
                setError(`Failed to fetch sub-product items: ${errorText}`);
                throw new Error(`Failed to fetch sub-product items: ${errorText}`);
            }

            const data = await response.json();
            if (Array.isArray(data)) setItems(data);
            else if (data && Array.isArray(data.data)) setItems(data.data);
            else setItems([]);
            setError('');
        } catch (error) {
            setError('Error fetching sub-product items');
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, [checkToken, handleLogout]);

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this sub-product item?')) {
            try {
                const token = checkToken();
                if (!token) return;

                const response = await fetch(`${API_BASE}/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    setError(`Error deleting sub-product item: ${errorText}`);
                    throw new Error(`Delete failed: ${errorText}`);
                }

                setError('');
                await fetchItems();
            } catch (error) {
                setError('Error deleting sub-product item');
                console.error('Delete error:', error);
            }
        }
    };

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    return (
        <div className="btc-container">
            <button className="btc-register-button" onClick={() => navigate('/sub-item/create')}>
                ลงทะเบียนสินค้า (Sub)
            </button>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <>
                    {error && <p className="error">{error}</p>}
                    <table className="btc-table">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>รหัสสินค้า</th>
                            <th>ชื่อสินค้า</th>
                            <th>รายละเอียด</th>
                            <th>ราคา</th>
                            <th>สถานะการขาย</th>
                            <th>ตัวเลือก</th>
                        </tr>
                        </thead>
                        <tbody>
                        {items.map(item => (
                            <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>{item.productCode}</td>
                                <td>{item.productName}</td>
                                <td>{item.description}</td>
                                <td>{item.price}</td>
                                <td>{item.active ? 'ขาย' : 'หยุดขาย'}</td>
                                <td>
                                    <RowActionsMenu
                                        actions={[
                                            {
                                                label: 'แก้ไข',
                                                icon: <i className="fas fa-edit"></i>,
                                                onClick: () => navigate(`/sub-item/edit/${item.id}`, { state: { subProductItem: item } }),
                                            },
                                            {
                                                label: 'ลบ',
                                                icon: <i className="fas fa-trash-alt"></i>,
                                                onClick: () => handleDelete(item.id),
                                                variant: 'danger',
                                            },
                                        ]}
                                    />
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

export default SubProductItemManagement;
